/**
 * useWebKeyboardViewport — the height a keyboard-aware screen should take on
 * mobile web, and whether the on-screen keyboard is up.
 *
 * iOS Safari reports the keyboard with ONE visualViewport resize at the END of
 * its ~250ms slide (nothing during it), then pans the page to reveal the
 * focused field based on the layout it saw BEFORE that resize. Measured on an
 * iOS 26 simulator with the chat layout: root still at full height when the
 * keyboard shows → Safari pans ~324px over ~230ms in ~15 steps, whatever the
 * layout does afterwards; a programmatic scroll reset during that pan cancels
 * it and Safari re-plans against the current layout. Fighting the pan on every
 * event (the previous pin-to-top hook) made the page jitter between the two
 * positions instead.
 *
 * So:
 * - On focusing a text field, jump straight to the keyboard-open height seen
 *   last time (remembered per session; half the viewport on a cold start), so
 *   the layout is already final when the keyboard arrives and Safari has
 *   nothing to reveal — measured: zero pan events, versus ~324px without.
 * - When the resize lands, settle to the measured height, remember it, and
 *   reconcile a stale pan once now and once when it has settled.
 * - If no keyboard shows up (hardware keyboard), undo the prediction.
 * - On blur, go back to full height immediately, not when the keyboard is gone.
 *
 * Natively the screen lets the navigator constrain it; the hook only reports
 * the window height there.
 */
import { useEffect, useState } from 'react';
import { Platform, useWindowDimensions } from 'react-native';

export const KEYBOARD_VIEWPORT_STORAGE_KEY = 'resio.webKeyboardViewportHeight';
/** A resize at least this much below the full height is the keyboard, not chrome. */
export const KEYBOARD_SHRINK_MIN_PX = 100;
/** No keyboard resize within this window after focus → there is no on-screen keyboard. */
export const KEYBOARD_PREDICTION_TIMEOUT_MS = 1200;
/** Safari's reveal pan runs ~230ms; reconcile once more after it. */
export const KEYBOARD_PAN_SETTLE_MS = 300;
/**
 * With nothing remembered yet (first focus of a session), assume the keyboard
 * takes about half the viewport. Guessing slightly LOW is the safe side: the
 * composer sits a few px above the keyboard and Safari has nothing to reveal;
 * guessing high leaves it under the keyboard and brings the pan back.
 */
export const KEYBOARD_FIRST_GUESS_RATIO = 0.5;

// The app's tsconfig has no DOM lib: browser globals are reached the way the
// rest of the web-facing code does, and these are the slices the hook touches.
declare const window: any;
declare const document: any;
declare const sessionStorage: any;

type ViewportLike = {
  height: number;
  scale: number;
  addEventListener: (type: 'resize', listener: () => void) => void;
  removeEventListener: (type: 'resize', listener: () => void) => void;
};
type Phase = 'idle' | 'opening' | 'open' | 'closing';

const NON_TEXT_INPUT_TYPES = new Set(['button', 'checkbox', 'radio', 'submit', 'reset', 'range', 'color', 'file', 'image']);

export function isTextField(el: any): boolean {
  if (!el || typeof el.tagName !== 'string') return false;
  const tag = el.tagName.toUpperCase();
  if (tag === 'TEXTAREA') return true;
  if (tag === 'INPUT') return !NON_TEXT_INPUT_TYPES.has(String(el.type || 'text').toLowerCase());
  return el.isContentEditable === true;
}

const viewportHeight = (vv: ViewportLike) => Math.round(vv.height * (vv.scale || 1));

const readRemembered = (): number => {
  try {
    const value = Number(sessionStorage.getItem(KEYBOARD_VIEWPORT_STORAGE_KEY));
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
};
const remember = (height: number) => {
  try {
    sessionStorage.setItem(KEYBOARD_VIEWPORT_STORAGE_KEY, String(height));
  } catch {
    // storage unavailable — prediction just starts cold next session
  }
};

export type KeyboardViewport = { height: number; keyboardOpen: boolean };

export function useWebKeyboardViewport(): KeyboardViewport {
  const isWeb = Platform.OS === 'web';
  const { height: windowHeight } = useWindowDimensions();
  const [state, setState] = useState<KeyboardViewport>(() => {
    const vv: ViewportLike | undefined = isWeb && typeof window !== 'undefined' ? window.visualViewport : undefined;
    return { height: vv ? viewportHeight(vv) : windowHeight, keyboardOpen: false };
  });

  useEffect(() => {
    if (!isWeb) return undefined;
    const win = typeof window === 'undefined' ? undefined : window;
    const doc = typeof document === 'undefined' ? undefined : document;
    const vv: ViewportLike | undefined = win?.visualViewport;
    if (!win || !doc || !vv) return undefined;

    let full = viewportHeight(vv);
    let remembered = readRemembered();
    let phase: Phase = 'idle';
    let predictionTimer: ReturnType<typeof setTimeout> | null = null;
    let settleTimer: ReturnType<typeof setTimeout> | null = null;
    const clearPrediction = () => {
      if (predictionTimer != null) clearTimeout(predictionTimer);
      predictionTimer = null;
    };

    const apply = (height: number, keyboardOpen: boolean) =>
      setState((prev) => (prev.height === height && prev.keyboardOpen === keyboardOpen ? prev : { height, keyboardOpen }));
    const reconcileScroll = () => {
      if (win.scrollY > 0) win.scrollTo(0, 0);
    };

    const onFocusIn = (e: any) => {
      if (!isTextField(e?.target)) return;
      phase = 'opening';
      apply(remembered || Math.round(full * KEYBOARD_FIRST_GUESS_RATIO), true);
      clearPrediction();
      predictionTimer = setTimeout(() => {
        predictionTimer = null;
        if (phase === 'opening') {
          phase = 'idle';
          apply(full, false);
        }
      }, KEYBOARD_PREDICTION_TIMEOUT_MS);
    };

    const onFocusOut = (e: any) => {
      if (!isTextField(e?.target) || isTextField(e?.relatedTarget)) return;
      phase = 'closing';
      clearPrediction();
      apply(full, false);
    };

    const onResize = () => {
      const height = viewportHeight(vv);
      if (height <= full - KEYBOARD_SHRINK_MIN_PX) {
        remembered = height;
        remember(height);
        phase = 'open';
        clearPrediction();
        apply(height, true);
        reconcileScroll();
        if (settleTimer != null) clearTimeout(settleTimer);
        settleTimer = setTimeout(() => {
          settleTimer = null;
          reconcileScroll();
        }, KEYBOARD_PAN_SETTLE_MS);
        return;
      }
      if (phase === 'opening') {
        // Browser chrome moved a little, or Chrome Android is animating the
        // shrink in steps: the keyboard is still coming. Never let these drag
        // the full height down, or the keyboard step could stop registering.
        if (height > full) full = height;
        return;
      }
      full = height;
      phase = 'idle';
      apply(height, false);
    };

    doc.addEventListener('focusin', onFocusIn);
    doc.addEventListener('focusout', onFocusOut);
    vv.addEventListener('resize', onResize);
    return () => {
      doc.removeEventListener('focusin', onFocusIn);
      doc.removeEventListener('focusout', onFocusOut);
      vv.removeEventListener('resize', onResize);
      clearPrediction();
      if (settleTimer != null) clearTimeout(settleTimer);
    };
  }, [isWeb]);

  return isWeb ? state : { height: windowHeight, keyboardOpen: false };
}
